import React, { useEffect, useRef, useState } from "react";
import {
  Avatar,
  Badge,
  Box,
  Button,
  Chip,
  Container,
  Divider,
  Fab,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Popover,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Add as AddIcon,
  ArrowForwardRounded as ArrowForwardRoundedIcon,
  CloseRounded as CloseRoundedIcon,
  DashboardRounded as DashboardRoundedIcon,
  FavoriteBorderRounded as FavoriteBorderRoundedIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  Logout as LogoutIcon,
  NotificationsNoneRounded as NotificationsIcon,
  PersonOutlineRounded as PersonOutlineRoundedIcon,
  SellOutlined as SellOutlinedIcon,
  SettingsOutlined as SettingsOutlinedIcon,
  ShoppingBagOutlined as ShoppingBagOutlinedIcon,
  VerifiedRounded as VerifiedRoundedIcon,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import { fetchUserByUniqueId } from "../../redux/actions/userActions";
import { CenteredLoading } from "../../components/common/CenteredLoading";
import { Breadcrumb } from "../../components/common/Breadcrumb";
import { SEO } from "../../components/common/SEO";
import {
  deleteLoginCookieAndSession,
  getLoginCookieAndSession,
  getSellerName,
  sendConfirmationEmail,
  setSession,
  getSession,
  deleteSession,
} from "../../utils";
import { USER_JUST_LOGGED_IN, USER_JUST_LOGGED_OUT } from "../../constants";
import SnackbarAlert, {
  SnackbarRef,
} from "../../components/common/SnackbarAlert";
import {
  fetchNotificationsByUser,
  updateNotificationsById,
} from "../../redux/actions/notificationActions";
import { Notification } from "../../redux/types/notificationTypes";
import { SORT_BY } from "../../redux/actions/productActions";
import { fetchCartReset } from "../../redux/actions/cartAction";
import { Dashboard } from "../../components/account/Dashboard";
import { MyListing } from "../../components/account/MyListing";
import { Purchase } from "../../components/account/Purchase";
import { Sold } from "../../components/account/Sold";
import { Wishlist } from "../../components/account/Wishlist";
import { Settings } from "../../components/account/Settings";
import { useStripeAccountStatus } from "../../hooks/useStripeAccountStatus";
import { createStripeAccountLink } from "../Seller/seller-setup-api";

const BRAND_ORANGE = "#e78758";
const BRAND_ORANGE_DARK = "#cf7045";
const PAGE_BACKGROUND = "#f5f7f9";

const Account: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();

  const {
    sellRoute,
    isApproved,
    needsAction,
    loading: stripeStatusLoading,
  } = useStripeAccountStatus();

  const [selectedTab, setSelectedTab] = useState(() => {
    const state = location.state as { tab?: number } | null;

    return typeof state?.tab === "number" ? state.tab : 0;
  });

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [defaultFilterInMyListing, setDefaultFilterInMyListing] =
    useState<SORT_BY>(SORT_BY.LATEST);
  const [verificationLinkLoading, setVerificationLinkLoading] = useState(false);

  const snackbarRef = useRef<SnackbarRef>(null);

  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const profileId = getLoginCookieAndSession();

  const { loading, users } = useSelector((state: RootState) => state.users);

  const { notificationsData } = useSelector(
    (state: RootState) => state.notifications,
  );

  useEffect(() => {
    if (profileId) {
      dispatch(fetchNotificationsByUser(Number(profileId)));
      dispatch(fetchUserByUniqueId(Number(profileId)));
    } else {
      deleteLoginCookieAndSession();
      navigate("/");
    }
  }, [dispatch, navigate, profileId]);

  useEffect(() => {
    setNotifications(notificationsData.notifications);
  }, [notificationsData.notifications]);

  /*
   * useStripeAccountStatus polls until the seller account is approved.
   * Once approved, refresh the user and notification records.
   */
  useEffect(() => {
    if (isApproved && profileId) {
      dispatch(fetchUserByUniqueId(Number(profileId)));
      dispatch(fetchNotificationsByUser(Number(profileId)));
    }
  }, [isApproved, profileId, dispatch]);

  /*
   * Refresh notifications when Stripe reports that seller action is required.
   */
  useEffect(() => {
    if (needsAction && profileId) {
      dispatch(fetchNotificationsByUser(Number(profileId)));
    }
  }, [needsAction, profileId, dispatch]);

  /*
   * Clear the confirmation notification once the user is verified.
   */
  useEffect(() => {
    if (
      users &&
      users.length > 0 &&
      users[0].isVerified &&
      notificationsData.notifications.length > 0
    ) {
      const confirmNotifications = notificationsData.notifications.filter(
        (notification) => notification.title?.includes("Confirm Your Account"),
      );

      confirmNotifications.forEach((notification) => {
        dispatch(updateNotificationsById(notification.documentId));
      });

      if (confirmNotifications.length > 0) {
        setNotifications((previousNotifications) =>
          previousNotifications.filter(
            (notification) =>
              !notification.title?.includes("Confirm Your Account"),
          ),
        );
      }
    }
  }, [users, notificationsData.notifications, dispatch]);

  /*
   * Clear the Stripe verification notification when it no longer applies.
   */
  useEffect(() => {
    if (
      !stripeStatusLoading &&
      !needsAction &&
      notificationsData.notifications.length > 0
    ) {
      const verificationNotifications = notificationsData.notifications.filter(
        (notification) =>
          notification.title?.includes("Seller Verification Required"),
      );

      verificationNotifications.forEach((notification) => {
        dispatch(updateNotificationsById(notification.documentId));
      });

      if (verificationNotifications.length > 0) {
        setNotifications((previousNotifications) =>
          previousNotifications.filter(
            (notification) =>
              !notification.title?.includes("Seller Verification Required"),
          ),
        );
      }
    }
  }, [
    stripeStatusLoading,
    needsAction,
    notificationsData.notifications,
    dispatch,
  ]);

  useEffect(() => {
    if (getSession(USER_JUST_LOGGED_IN)) {
      snackbarRef.current?.showSnackbar("Signed in successfully", "success");

      const timer = setTimeout(() => {
        deleteSession(USER_JUST_LOGGED_IN);
      }, 2000);

      return () => {
        clearTimeout(timer);
      };
    }
  }, []);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setSelectedTab(newValue);
    setDefaultFilterInMyListing(SORT_BY.LATEST);
  };

  const handleLogout = () => {
    deleteLoginCookieAndSession();
    setSession(USER_JUST_LOGGED_OUT, USER_JUST_LOGGED_OUT);
    dispatch(fetchCartReset());
    navigate("/");
  };

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleDismissNotification = (id: string) => {
    dispatch(updateNotificationsById(id));

    setNotifications((previousNotifications) =>
      previousNotifications.filter(
        (notification) => notification.documentId !== id,
      ),
    );
  };

  /*
   * Generate a fresh Stripe account link immediately before redirecting.
   */
  const handleCompleteVerificationOnStripe = async () => {
    if (!profileId || verificationLinkLoading) {
      return;
    }

    setVerificationLinkLoading(true);

    try {
      const returnUrl = `${window.location.origin}/account?refresh=true`;

      const { url } = await createStripeAccountLink(
        Number(profileId),
        returnUrl,
      );

      handleClose();
      window.location.href = url;
    } catch (error) {
      setVerificationLinkLoading(false);

      snackbarRef.current?.showSnackbar(
        error instanceof Error
          ? error.message
          : "Failed to open verification page. Please try again.",
        "error",
      );
    }
  };

  const sendEmailAgain = async () => {
    try {
      if (users && users.length > 0) {
        await sendConfirmationEmail(users[0].email, users[0].createdTime);

        snackbarRef.current?.showSnackbar(
          "Confirmation email resent. Please check your inbox.",
          "success",
        );
      }
    } catch (error) {
      console.error(error);

      snackbarRef.current?.showSnackbar(
        "We could not resend the confirmation email. Please try again.",
        "error",
      );
    }
  };

  const open = Boolean(anchorEl);

  if (!loading && users && users.length <= 0) {
    deleteLoginCookieAndSession();
    navigate("/");
    return null;
  }

  if (!users || users.length === 0) {
    return <CenteredLoading />;
  }

  const sellerDisplayName = getSellerName(users[0].name);
  const sellerInitial = sellerDisplayName?.charAt(0).toUpperCase() || "U";

  const statusLabel = needsAction
    ? "Seller action required"
    : isApproved
      ? "Seller verified"
      : users[0].isVerified
        ? "Account verified"
        : "Confirmation pending";

  const statusColor = needsAction
    ? "#ffc36f"
    : isApproved || users[0].isVerified
      ? "#79e0aa"
      : "#ffd18f";

  return (
    <>
      <SEO
        title="My Account Page • FiddlePiddle • Toys Marketplace"
        description="Manage your FiddlePiddle account, purchases, sales, toy listings, wishlist, seller information, and account settings."
        keywordsContent="toys marketplace, buy toys, sell toys, collectible toys, vintage toys, kids toys, toy deals, rare toys, toy shop, toy sales, toy collectors"
      />

      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: PAGE_BACKGROUND,
        }}
      >
        <Box
          sx={{
            position: "relative",
            zIndex: 100,
            bgcolor: "#ffffff",
          }}
        >
          <Breadcrumb
            items={[
              { text: "Home", url: "/" },
              { text: "My Account", url: "" },
            ]}
          />
        </Box>

        <Container
          maxWidth="lg"
          sx={{
            pt: {
              xs: 2,
              sm: 3,
            },
            pb: {
              xs: 5,
              md: 6,
            },
          }}
        >
          <SnackbarAlert ref={snackbarRef} />

          {/* Account header */}
          <Box
            sx={{
              position: "relative",
              overflow: "hidden",
              mb: 3,
              borderRadius: {
                xs: "22px",
                md: "30px",
              },
              color: "#ffffff",
              background:
                "linear-gradient(135deg, #1b252d 0%, #293945 55%, #354b58 100%)",
              boxShadow: "0 22px 55px rgba(24, 34, 42, 0.2)",

              "&::before": {
                content: '""',
                position: "absolute",
                width: {
                  xs: 300,
                  md: 500,
                },
                height: {
                  xs: 300,
                  md: 500,
                },
                top: {
                  xs: -170,
                  md: -280,
                },
                right: {
                  xs: -150,
                  md: -160,
                },
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(231,135,88,0.62) 0%, rgba(231,135,88,0) 70%)",
                pointerEvents: "none",
              },

              "&::after": {
                content: '""',
                position: "absolute",
                inset: 0,
                opacity: 0.18,
                backgroundImage: `
                  linear-gradient(
                    rgba(255,255,255,0.08) 1px,
                    transparent 1px
                  ),
                  linear-gradient(
                    90deg,
                    rgba(255,255,255,0.08) 1px,
                    transparent 1px
                  )
                `,
                backgroundSize: "50px 50px",
                maskImage:
                  "linear-gradient(to right, rgba(0,0,0,0.8), transparent)",
                pointerEvents: "none",
              },
            }}
          >
            <Box
              sx={{
                position: "relative",
                zIndex: 1,
                display: "flex",
                flexDirection: {
                  xs: "column",
                  md: "row",
                },
                alignItems: {
                  xs: "stretch",
                  md: "center",
                },
                justifyContent: "space-between",
                gap: {
                  xs: 3,
                  md: 4,
                },
                p: {
                  xs: 2.5,
                  sm: 3.5,
                  md: 4,
                },
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: {
                    xs: "column",
                    sm: "row",
                  },
                  alignItems: {
                    xs: "center",
                    sm: "center",
                  },
                  gap: {
                    xs: 2,
                    sm: 2.5,
                  },
                  minWidth: 0,
                  textAlign: {
                    xs: "center",
                    sm: "left",
                  },
                }}
              >
                <Avatar
                  sx={{
                    width: {
                      xs: 78,
                      sm: 92,
                    },
                    height: {
                      xs: 78,
                      sm: 92,
                    },
                    flexShrink: 0,
                    fontSize: {
                      xs: "1.9rem",
                      sm: "2.25rem",
                    },
                    fontWeight: 900,
                    color: "#ffffff",
                    background:
                      "linear-gradient(135deg, #f3a078 0%, #d66d3d 100%)",
                    border: "5px solid rgba(255,255,255,0.18)",
                    boxShadow: "0 15px 34px rgba(0,0,0,0.26)",
                  }}
                >
                  {sellerInitial}
                </Avatar>

                <Box sx={{ minWidth: 0 }}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: {
                        xs: "column",
                        sm: "row",
                      },
                      alignItems: {
                        xs: "center",
                        sm: "center",
                      },
                      gap: 1,
                      mb: 0.75,
                    }}
                  >
                    <Typography
                      component="h1"
                      sx={{
                        fontSize: {
                          xs: "1.85rem",
                          sm: "2.25rem",
                          md: "2.55rem",
                        },
                        lineHeight: 1.1,
                        fontWeight: 900,
                        letterSpacing: "-0.035em",
                        wordBreak: "break-word",
                      }}
                    >
                      Hi, {sellerDisplayName}!
                    </Typography>

                    <Chip
                      size="small"
                      icon={<VerifiedRoundedIcon />}
                      label={statusLabel}
                      sx={{
                        color: "#ffffff",
                        bgcolor: needsAction
                          ? "rgba(255,175,75,0.16)"
                          : "rgba(72,190,132,0.14)",
                        border: `1px solid ${statusColor}40`,

                        "& .MuiChip-icon": {
                          color: statusColor,
                        },
                      }}
                    />
                  </Box>

                  <Typography
                    variant="body1"
                    sx={{
                      maxWidth: 590,
                      mb: 1.75,
                      color: "rgba(255,255,255,0.72)",
                      lineHeight: 1.6,
                    }}
                  >
                    Manage your purchases, sales, listings, wishlist, and
                    account settings from one place.
                  </Typography>

                  <Button
                    variant="text"
                    size="small"
                    startIcon={<PersonOutlineRoundedIcon />}
                    endIcon={<ArrowForwardRoundedIcon />}
                    onClick={() =>
                      navigate(`/u/${users[0].createdTime}/profile`)
                    }
                    sx={{
                      minHeight: 36,
                      px: 1.4,
                      py: 0.5,
                      borderRadius: "999px",
                      color: "#ffffff",
                      bgcolor: "rgba(255,255,255,0.1)",
                      border: "1px solid rgba(255,255,255,0.14)",
                      fontSize: "0.82rem",
                      fontWeight: 700,
                      textTransform: "none",

                      "&:hover": {
                        color: "#ffffff",
                        bgcolor: "rgba(255,255,255,0.17)",
                      },
                    }}
                  >
                    View Public Profile
                  </Button>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  justifyContent: {
                    xs: "center",
                    md: "flex-end",
                  },
                  alignItems: "center",
                  gap: 1.25,
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => navigate(sellRoute)}
                  sx={{
                    display: {
                      xs: "none",
                      sm: "inline-flex",
                    },
                    minHeight: 44,
                    px: 2.25,
                    borderRadius: "999px",
                    color: "#ffffff",
                    bgcolor: BRAND_ORANGE,
                    fontWeight: 800,
                    textTransform: "none",
                    boxShadow: "0 12px 28px rgba(231,135,88,0.3)",

                    "&:hover": {
                      bgcolor: BRAND_ORANGE_DARK,
                      boxShadow: "0 15px 32px rgba(231,135,88,0.4)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  Sell a Toy
                </Button>

                <Tooltip title="Notifications">
                  <IconButton
                    aria-label="Open notifications"
                    onClick={handleOpen}
                    sx={{
                      width: 46,
                      height: 46,
                      color: "#ffffff",
                      bgcolor: "rgba(255,255,255,0.1)",
                      border: "1px solid rgba(255,255,255,0.14)",

                      "&:hover": {
                        bgcolor: "rgba(255,255,255,0.17)",
                      },
                    }}
                  >
                    <Badge
                      badgeContent={notifications.length}
                      color="primary"
                      sx={{
                        "& .MuiBadge-badge": {
                          minWidth: 18,
                          height: 18,
                          px: 0.5,
                          color: "#ffffff",
                          fontSize: "0.68rem",
                          fontWeight: 800,
                          bgcolor: BRAND_ORANGE,
                        },
                      }}
                    >
                      <NotificationsIcon />
                    </Badge>
                  </IconButton>
                </Tooltip>

                <Button
                  variant="outlined"
                  startIcon={<LogoutIcon />}
                  onClick={handleLogout}
                  sx={{
                    minHeight: 46,
                    px: 2.1,
                    borderRadius: "999px",
                    color: "#ffffff",
                    borderColor: "rgba(255,255,255,0.3)",
                    fontWeight: 750,
                    textTransform: "none",
                    whiteSpace: "nowrap",
                    bgcolor: "rgba(255,255,255,0.04)",

                    "&:hover": {
                      color: "#ffffff",
                      borderColor: "rgba(255,255,255,0.65)",
                      bgcolor: "rgba(255,255,255,0.1)",
                    },
                  }}
                >
                  Logout
                </Button>

                <Popover
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleClose}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "right",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                  PaperProps={{
                    sx: {
                      width: {
                        xs: "calc(100vw - 32px)",
                        sm: 380,
                      },
                      maxWidth: 380,
                      mt: 1,
                      overflow: "hidden",
                      borderRadius: "20px",
                      border: "1px solid #e3e7eb",
                      boxShadow: "0 24px 60px rgba(28,38,46,0.22)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                      px: 2,
                      py: 1.75,
                      bgcolor: "#ffffff",
                    }}
                  >
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 850,
                          lineHeight: 1.2,
                        }}
                      >
                        Notifications
                      </Typography>

                      <Typography variant="caption" color="text.secondary">
                        Account and seller updates
                      </Typography>
                    </Box>

                    <Chip
                      size="small"
                      label={notifications.length}
                      sx={{
                        minWidth: 32,
                        color: BRAND_ORANGE_DARK,
                        bgcolor: "rgba(231,135,88,0.12)",
                        fontWeight: 800,
                      }}
                    />
                  </Box>

                  <Divider />

                  {notifications.length > 0 ? (
                    <List
                      disablePadding
                      sx={{
                        maxHeight: 430,
                        overflowY: "auto",
                        bgcolor: "#ffffff",
                      }}
                    >
                      {notifications.map((notification, index) => (
                        <React.Fragment key={notification.documentId}>
                          <ListItem
                            alignItems="flex-start"
                            sx={{
                              px: 2,
                              py: 1.75,
                              pr: 6,
                              transition: "background-color 0.2s ease",

                              "&:hover": {
                                bgcolor: "#f8f9fa",
                              },
                            }}
                            secondaryAction={
                              <Tooltip title="Dismiss">
                                <IconButton
                                  edge="end"
                                  size="small"
                                  aria-label="Dismiss notification"
                                  onClick={() =>
                                    handleDismissNotification(
                                      notification.documentId,
                                    )
                                  }
                                  sx={{
                                    color: "text.secondary",

                                    "&:hover": {
                                      color: "#b84f35",
                                      bgcolor: "rgba(184,79,53,0.08)",
                                    },
                                  }}
                                >
                                  <CloseRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            }
                          >
                            <ListItemText
                              primary={
                                <Typography
                                  variant="body2"
                                  sx={{
                                    pr: 1,
                                    fontWeight: 800,
                                    color: "text.primary",
                                  }}
                                >
                                  {notification.title}
                                </Typography>
                              }
                              secondary={
                                <Box sx={{ mt: 0.65 }}>
                                  <Typography
                                    component="div"
                                    variant="body2"
                                    sx={{
                                      color: "text.secondary",
                                      lineHeight: 1.55,
                                    }}
                                  >
                                    {notification.title.includes(
                                      "Confirm Your Account",
                                    ) ? (
                                      <>
                                        Check your email and click “Confirm”
                                        within 48 hours to activate your
                                        account, or it will be deleted.{" "}
                                        <Link
                                          onClick={sendEmailAgain}
                                          variant="body2"
                                          className="clickableLink"
                                          sx={{
                                            cursor: "pointer",
                                            fontWeight: 750,
                                          }}
                                        >
                                          Resend confirmation
                                        </Link>
                                        .
                                      </>
                                    ) : notification.title.includes(
                                        "Seller Verification Required",
                                      ) ? (
                                      <>
                                        {notification.message}{" "}
                                        <Link
                                          onClick={
                                            handleCompleteVerificationOnStripe
                                          }
                                          variant="body2"
                                          className="clickableLink"
                                          sx={{
                                            cursor: verificationLinkLoading
                                              ? "default"
                                              : "pointer",
                                            fontWeight: 750,
                                          }}
                                        >
                                          {verificationLinkLoading
                                            ? "Opening…"
                                            : "Complete verification on Stripe"}
                                        </Link>
                                      </>
                                    ) : notification.title.includes(
                                        "Seller Account Approved",
                                      ) ? (
                                      <>
                                        {notification.message}{" "}
                                        <Link
                                          onClick={() => {
                                            handleClose();
                                            navigate("/sell");
                                          }}
                                          variant="body2"
                                          className="clickableLink"
                                          sx={{
                                            cursor: "pointer",
                                            fontWeight: 750,
                                          }}
                                        >
                                          Go to Seller Dashboard
                                        </Link>
                                      </>
                                    ) : (
                                      notification.message
                                    )}
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    sx={{
                                      display: "block",
                                      mt: 0.7,
                                      color: "text.disabled",
                                    }}
                                  >
                                    {notification.timeAgo}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>

                          {index < notifications.length - 1 && (
                            <Divider component="li" />
                          )}
                        </React.Fragment>
                      ))}
                    </List>
                  ) : (
                    <Box
                      sx={{
                        px: 3,
                        py: 5,
                        textAlign: "center",
                        bgcolor: "#ffffff",
                      }}
                    >
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          mx: "auto",
                          mb: 1.5,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "18px",
                          color: BRAND_ORANGE_DARK,
                          bgcolor: "rgba(231,135,88,0.12)",
                        }}
                      >
                        <NotificationsIcon />
                      </Box>

                      <Typography
                        variant="subtitle2"
                        sx={{
                          mb: 0.5,
                          fontWeight: 800,
                        }}
                      >
                        You’re all caught up
                      </Typography>

                      <Typography variant="body2" color="text.secondary">
                        You have no notifications at the moment.
                      </Typography>
                    </Box>
                  )}
                </Popover>
              </Box>
            </Box>
          </Box>

          {/* Account navigation */}
          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              mb: 3,
              mx: {
                xs: -2,
                sm: 0,
              },
              px: {
                xs: 1,
                sm: 1.25,
              },
              py: 1,
              overflow: "hidden",
              borderRadius: {
                xs: 0,
                sm: "20px",
              },
              bgcolor: "#ffffff",
              border: {
                xs: "1px solid #e6e9ed",
                sm: "1px solid #e1e5e9",
              },
              boxShadow: "0 12px 34px rgba(36,46,54,0.07)",
            }}
          >
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant={isMobile ? "scrollable" : "fullWidth"}
              scrollButtons
              allowScrollButtonsMobile
              aria-label="Account sections"
              sx={{
                width: "100%",
                minHeight: 52,

                "& .MuiTabs-indicator": {
                  display: "none",
                },

                "& .MuiTabs-scrollButtons": {
                  color: "text.secondary",
                },

                "& .MuiTab-root": {
                  minHeight: 48,
                  minWidth: {
                    xs: 112,
                    sm: 0,
                  },
                  mx: 0.35,
                  px: {
                    xs: 1.5,
                    sm: 1,
                  },
                  borderRadius: "14px",
                  color: "#66717a",
                  fontSize: {
                    xs: "0.78rem",
                    md: "0.84rem",
                  },
                  fontWeight: 750,
                  textTransform: "none",
                  transition:
                    "background-color 0.2s ease, color 0.2s ease, transform 0.2s ease",

                  "& .MuiSvgIcon-root": {
                    mr: 0.7,
                    fontSize: "1.15rem",
                  },

                  "&:hover": {
                    color: BRAND_ORANGE_DARK,
                    bgcolor: "rgba(231,135,88,0.07)",
                  },

                  "&.Mui-selected": {
                    color: BRAND_ORANGE_DARK,
                    bgcolor: "rgba(231,135,88,0.13)",
                    boxShadow: "inset 0 0 0 1px rgba(231,135,88,0.14)",
                  },
                },
              }}
            >
              <Tab
                icon={<DashboardRoundedIcon />}
                iconPosition="start"
                label="Overview"
              />

              <Tab
                icon={<ShoppingBagOutlinedIcon />}
                iconPosition="start"
                label="Purchases"
              />

              <Tab
                icon={<SellOutlinedIcon />}
                iconPosition="start"
                label="Sold"
              />

              <Tab
                icon={<Inventory2OutlinedIcon />}
                iconPosition="start"
                label="My Listings"
              />

              <Tab
                icon={<FavoriteBorderRoundedIcon />}
                iconPosition="start"
                label="Wishlist"
              />

              <Tab
                icon={<SettingsOutlinedIcon />}
                iconPosition="start"
                label="Settings"
              />
            </Tabs>
          </Box>

          {/* Account tab content */}
          <Box
            sx={{
              minHeight: 360,
              borderRadius: {
                xs: "18px",
                sm: "22px",
              },
            }}
          >
            {selectedTab === 0 ? (
              <Dashboard
                profileId={profileId}
                setSelectedTab={setSelectedTab}
                setDefaultFilterInMyListing={setDefaultFilterInMyListing}
              />
            ) : null}

            {selectedTab === 1 && <Purchase />}

            {selectedTab === 2 && <Sold />}

            {selectedTab === 3 ? (
              <MyListing
                profileId={profileId}
                defaultFilter={defaultFilterInMyListing}
              />
            ) : null}

            {selectedTab === 4 && <Wishlist profileId={profileId} />}

            {selectedTab === 5 && <Settings userEmail={users[0].email} />}
          </Box>

          {/* Floating selling action */}
          <Tooltip
            title={isMobile ? "Post a Toy for Sale" : ""}
            placement="left"
          >
            <Fab
              variant={isMobile ? "circular" : "extended"}
              aria-label="Post a toy for sale"
              onClick={() => navigate(sellRoute)}
              sx={{
                position: "fixed",
                right: {
                  xs: 16,
                  sm: 28,
                  lg: 46,
                },
                bottom: {
                  xs: 72,
                  sm: 36,
                },
                zIndex: 99,
                minWidth: isMobile ? 56 : 150,
                height: 56,
                px: isMobile ? 0 : 2.5,
                color: "#ffffff",
                background: "linear-gradient(135deg, #f1986c 0%, #dd7242 100%)",
                fontWeight: 850,
                textTransform: "none",
                boxShadow: "0 16px 36px rgba(221,114,66,0.4)",

                "&:hover": {
                  color: "#ffffff",
                  background:
                    "linear-gradient(135deg, #f6a47b 0%, #cf6536 100%)",
                  boxShadow: "0 19px 42px rgba(221,114,66,0.5)",
                  transform: "translateY(-2px)",
                },
              }}
            >
              <AddIcon
                sx={{
                  mr: isMobile ? 0 : 1,
                }}
              />

              {!isMobile && "Sell a Toy"}
            </Fab>
          </Tooltip>
        </Container>
      </Box>
    </>
  );
};

export default Account;
